import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { router, usePage } from '@inertiajs/react';
import { RefreshCw, XCircle } from 'lucide-react';
import { UserProps } from '@/types/questionnaire';
import { useMutation } from '@tanstack/react-query';
import { questionnaireApi } from '@/lib/api/questionnaireApi';
import { toast } from 'sonner';

interface RejectedProps {
  user: {
    admin_rejected: boolean;
    rejection_reason: string | null;
    rejected_at: string;
  };
  can_reapply: boolean;
}

export default function Rejected({ user, can_reapply }: RejectedProps) {
  const { current_user } = usePage().props;
  const rejectedDate = new Date(user.rejected_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const { mutate: reapply, isPending } = useMutation({
    mutationFn: questionnaireApi.reapply,
    onSuccess: (data) => {
      router.visit(data.redirect);
    },
    onError: (error: any) => {
      toast.error('Error', {
        description:
          error.response?.data?.error || 'Failed to start new application',
      });
    },
  });

  const handleReapply = () => {
    // This will be implemented when we add the reapplication endpoint
    reapply();
  };

  return (
    <div className="min-h-screen py-8 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-semibold">
                Application Status
              </CardTitle>
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                <XCircle className="h-3 w-3 mr-1" />
                Not Approved
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Review Date */}
            <div className="text-sm text-muted-foreground">
              Reviewed on {rejectedDate}
            </div>

            {/* Feedback */}
            {user.rejection_reason && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-900 mb-2">
                  Feedback from Review Team
                </h3>
                <p className="text-sm text-red-800">{user.rejection_reason}</p>
              </div>
            )}

            {/* Reapply Option */}
            {can_reapply && (
              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-4">
                  <h3 className="text-sm font-medium mb-1">You Can Reapply</h3>
                  <p className="text-sm text-muted-foreground">
                    Address the feedback above and submit a new application
                  </p>
                </div>
                <Button onClick={handleReapply} className="w-full" size="lg">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {isPending ? 'Starting...' : 'Start New Application'}
                </Button>
              </div>
            )}

            {/* Support */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Need help?{' '}
                <a
                  href={
                    (current_user as UserProps).category == 'graduate'
                      ? 'mailto:pregrad@project-short.com'
                      : 'mailto:prehealth@project-short.com'
                  }
                  className="text-primary hover:underline"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
